import * as THREE from 'three';
import { ModelLoader, modelLoader } from '@/render/ModelLoader';
import { VEHICLES } from '@/entities/vehicles';

/**
 * trophy.ts — the WORLD TOUR CUP as live 3D (M80 title screen; M82b shape).
 *
 * Built PROCEDURALLY in three.js instead of a GLB: the title stage needs the
 * cradle's exact coordinates for the globe bounce-in animation, the geometry
 * is a few kB of code instead of a model download on the flagship screen, and
 * every proportion is a named knob below.
 *
 * M82b (owner): the silhouette now reads like the REAL World Cup — a gold
 * base wearing two deep-green bands (the malachite rings), a waisted solid
 * gold body that flares upward into the crown whose rim SEATS the globe —
 * with the game's own twist kept: the spiral race-track road climbing the
 * front, ridden by the gold-plated sports car, nose toward the ball.
 *
 * The globe itself is NOT built here — the stage seats the shared Globe.ts
 * earth (scaled) at `cradle`, so the trophy always wears the same planet as
 * the tour flights.
 */

// ---- Proportion knobs (trophy-local units, base sits on y=0) --------------
const GLOBE_SEAT_RADIUS = 1.5; // the seated globe's radius
/** The crown's rim circle — the ball SEATS on it, so the ball-centre height
 *  is derived: rimY + sqrt(ballR² − rimR²). */
const RIM_RADIUS = 1.05;
const RIM_Y = 4.28;
const BALL_CENTER_Y = RIM_Y + Math.sqrt(GLOBE_SEAT_RADIUS ** 2 - RIM_RADIUS ** 2);

export interface TrophyBuild {
  group: THREE.Group;
  /** Local-space point where the globe's CENTRE seats (feed to the stage). */
  cradle: THREE.Vector3;
  /** The radius the seated globe should be scaled to. */
  globeRadius: number;
  /** Overall height including the seated globe (camera framing). */
  height: number;
  dispose(): void;
}

/** Polished gold — metalness 1 needs a scene.environment to live (the stage
 *  provides IBL; without it gold reads as black plastic — the M35 lesson). */
function goldMaterial(rough = 0.24): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0xe2b04a,
    metalness: 1,
    roughness: rough,
    envMapIntensity: 1.7,
  });
}

/** Turn a loaded vehicle GLB into a solid-gold statuette (M82): every part
 *  gold, with glass/tyres a touch darker + rougher so the body's polish pops.
 *  New materials are pushed onto `materials` for disposal. */
function goldPlate(root: THREE.Object3D, materials: THREE.Material[]): void {
  const bright = new THREE.MeshStandardMaterial({
    color: 0xf3c25c, // hotter than the ribbons — the statuette must pop
    metalness: 1,
    roughness: 0.18,
    envMapIntensity: 2.2,
  });
  const dark = new THREE.MeshStandardMaterial({
    color: 0x8a6420,
    metalness: 1,
    roughness: 0.42,
    envMapIntensity: 1.3,
  });
  materials.push(bright, dark);
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const name = ((mesh.material as THREE.Material)?.name ?? '').toLowerCase();
    mesh.material =
      name.includes('glass') || name.includes('tyre') || name.includes('visor') ? dark : bright;
  });
}

/** Sweep a closed box-strip (4 corner vertices per sample ring, quads between
 *  rings, capped ends) along `centers` with a caller-supplied frame. Frames
 *  are hand-built — Frenet frames (ExtrudeGeometry) roll unpredictably on
 *  spirals (measured, M80). */
function sweepBoxStrip(
  centers: THREE.Vector3[],
  frameAt: (i: number, T: THREE.Vector3, W: THREE.Vector3, N: THREE.Vector3) => void,
  halfWidthAt: (t: number) => number,
  halfThick: number,
): THREE.BufferGeometry {
  const n = centers.length - 1;
  const positions: number[] = [];
  const indices: number[] = [];
  const W = new THREE.Vector3();
  const N = new THREE.Vector3();
  const T = new THREE.Vector3();
  for (let i = 0; i <= n; i++) {
    const c = centers[i];
    const prev = centers[Math.max(0, i - 1)];
    const next = centers[Math.min(n, i + 1)];
    T.subVectors(next, prev).normalize();
    frameAt(i, T, W, N);
    const hw = halfWidthAt(i / n);
    for (const [sw, sn] of [
      [1, 1],
      [-1, 1],
      [-1, -1],
      [1, -1],
    ] as const) {
      positions.push(
        c.x + W.x * hw * sw + N.x * halfThick * sn,
        c.y + W.y * hw * sw + N.y * halfThick * sn,
        c.z + W.z * hw * sw + N.z * halfThick * sn,
      );
    }
  }
  for (let i = 0; i < n; i++) {
    const a = i * 4;
    const b = a + 4;
    for (let e = 0; e < 4; e++) {
      const e2 = (e + 1) % 4;
      indices.push(a + e, b + e, b + e2, a + e, b + e2, a + e2);
    }
  }
  const last = n * 4;
  indices.push(0, 1, 2, 0, 2, 3);
  indices.push(last, last + 2, last + 1, last, last + 3, last + 2);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  return geo;
}

/** Sample a Catmull-Rom spline through control points into N+1 centers. */
function sampleSpline(control: THREE.Vector3[], samples: number): THREE.Vector3[] {
  const spline = new THREE.CatmullRomCurve3(control, false, 'centripetal');
  const centers: THREE.Vector3[] = [];
  for (let i = 0; i <= samples; i++) centers.push(spline.getPoint(i / samples));
  return centers;
}

/** The World-Cup body (M82b): a solid gold lathe — rising from the base,
 *  waisting in, then flaring into the crown whose rim seats the globe.
 *  Profile control points [radius, height], splined smooth. */
const BODY_PROFILE: [number, number][] = [
  [1.15, 1.3],
  [0.8, 1.78],
  [0.55, 2.35],
  [0.48, 2.9],
  [0.58, 3.45],
  [0.86, 3.9],
  [1.16, 4.14],
  [RIM_RADIUS, RIM_Y],
];

function bodyGeometry(): THREE.BufferGeometry {
  const spline = new THREE.CatmullRomCurve3(
    BODY_PROFILE.map(([r, y]) => new THREE.Vector3(r, y, 0)),
    false,
    'centripetal',
  );
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i <= 48; i++) {
    const p = spline.getPoint(i / 48);
    pts.push(new THREE.Vector2(Math.max(0.01, p.x), p.y));
  }
  return new THREE.LatheGeometry(pts, 64);
}

// ---------------------------------------------------------------------------
// The RACE TRACK ramp (M82, owner: "a golden racecar riding up the trophy,
// nose toward the globe"): a road-like spiral sweep — face normal UP, width
// horizontal — climbing from the base around the front to just under the
// crown, with the game's own sports car (gold-plated) riding its top end.
// ---------------------------------------------------------------------------

/** Control rows [angle (rad, x=cos·r / z=sin·r), radius, height]. The camera
 *  looks from +Z, so the ramp's climb crosses the FRONT of the trophy. */
const RAMP_ROWS: [number, number, number][] = [
  [-2.6, 1.9, 1.42],
  [-1.5, 1.88, 2.2],
  [-0.4, 1.66, 3.0],
  [0.5, 1.5, 3.78],
  [1.05, 1.44, 4.14],
];
const RAMP_SAMPLES = 70;
const RAMP_HALF_WIDTH = 0.52;
/** Where the car sits along the ramp (0..1) + how much its nose pulls toward
 *  the globe at the top. Mid-climb keeps the car proud of the silhouette —
 *  parked at the very top it hid behind the ball (measured). */
const CAR_AT = 0.72;
const CAR_AIM_AT_BALL = 0.35;
const CAR_LENGTH = 2.1;

function rampCenters(): THREE.Vector3[] {
  return sampleSpline(
    RAMP_ROWS.map(([a, r, y]) => new THREE.Vector3(Math.cos(a) * r, y, Math.sin(a) * r)),
    RAMP_SAMPLES,
  );
}

function rampGeometry(centers: THREE.Vector3[]): THREE.BufferGeometry {
  const up = new THREE.Vector3(0, 1, 0);
  return sweepBoxStrip(
    centers,
    (_i, T, W, N) => {
      W.crossVectors(up, T).normalize();
      N.crossVectors(T, W).normalize();
    },
    // A gentle flare at the launch end, constant road width above.
    (t) => RAMP_HALF_WIDTH * (1 + 0.35 * Math.max(0, 1 - t * 3)),
    0.05,
  );
}

export function buildTrophy(): TrophyBuild {
  const group = new THREE.Group();
  const geometries: THREE.BufferGeometry[] = [];
  const materials: THREE.Material[] = [];
  const gold = goldMaterial();
  const goldSoft = goldMaterial(0.34);
  // The malachite-style bands of the real World Cup, in the game's pitch
  // green — deep, glossy, stone-like.
  const greenBand = new THREE.MeshPhysicalMaterial({
    color: 0x155c2e,
    metalness: 0.25,
    roughness: 0.2,
    clearcoat: 0.9,
    clearcoatRoughness: 0.18,
  });
  materials.push(gold, goldSoft, greenBand);

  const add = (geo: THREE.BufferGeometry, mat: THREE.Material, y: number): THREE.Mesh => {
    geometries.push(geo);
    const m = new THREE.Mesh(geo, mat);
    m.position.y = y;
    group.add(m);
    return m;
  };

  // ---- Base (M82b, World-Cup language): gold tiers wearing two deep-green
  // bands, tapering up into the body. ----
  add(new THREE.CylinderGeometry(1.72, 1.88, 0.4, 64), goldSoft, 0.2);
  add(new THREE.CylinderGeometry(1.7, 1.7, 0.3, 64), greenBand, 0.55);
  add(new THREE.CylinderGeometry(1.72, 1.72, 0.08, 64), gold, 0.74);
  add(new THREE.CylinderGeometry(1.56, 1.56, 0.26, 64), greenBand, 0.91);
  add(new THREE.CylinderGeometry(1.28, 1.5, 0.32, 64), goldSoft, 1.2);

  // ---- The solid gold body: waist → flaring crown, rim seats the globe. ----
  const body = bodyGeometry();
  geometries.push(body);
  group.add(new THREE.Mesh(body, gold));

  // ---- The RACE-TRACK ramp spiralling up the front (M82). Its own material:
  // a touch less metallic so the road holds warm gold even on the shadow side
  // (full metal read near-black there — measured). ----
  const roadGold = new THREE.MeshStandardMaterial({
    color: 0xd8a844,
    metalness: 0.82,
    roughness: 0.34,
    envMapIntensity: 2.0,
  });
  materials.push(roadGold);
  const centers = rampCenters();
  const ramp = rampGeometry(centers);
  geometries.push(ramp);
  group.add(new THREE.Mesh(ramp, roadGold));

  // ---- The golden racecar riding the ramp, nose toward the globe. ----
  // The game's own sports GLB, gold-plated (async — the trophy shows at once,
  // the car rolls in when the model lands; a load failure just leaves it off).
  const carHolder = new THREE.Group();
  {
    const idx = Math.min(centers.length - 1, Math.round(CAR_AT * centers.length));
    const at = centers[idx];
    const tangent = centers[Math.min(centers.length - 1, idx + 2)]
      .clone()
      .sub(centers[Math.max(0, idx - 2)])
      .normalize();
    const toBall = new THREE.Vector3(0, BALL_CENTER_Y, 0).sub(at).normalize();
    const forward = tangent.lerp(toBall, CAR_AIM_AT_BALL).normalize();
    const side = new THREE.Vector3().crossVectors(new THREE.Vector3(0, 1, 0), forward).normalize();
    const carUp = new THREE.Vector3().crossVectors(forward, side).normalize();
    carHolder.position.copy(at).addScaledVector(carUp, 0.05);
    carHolder.quaternion.setFromRotationMatrix(
      new THREE.Matrix4().makeBasis(side, carUp, forward),
    );
    group.add(carHolder);
    void modelLoader
      .load(VEHICLES.sports.url)
      .then((g) => {
        const fitted = ModelLoader.fit(g, CAR_LENGTH, VEHICLES.sports.forwardIsZ);
        goldPlate(fitted, materials);
        carHolder.add(fitted);
      })
      .catch(() => {
        /* no car — the trophy still stands */
      });
  }

  // ---- A crisp gold torus trims the crown rim (the ball's seat line). ----
  const ring = add(new THREE.TorusGeometry(RIM_RADIUS, 0.07, 14, 48), gold, RIM_Y);
  ring.rotation.x = Math.PI / 2;

  // ---- The engraved plate: WORLD TOUR CUP on the base's lower green band. ----
  const plateCanvas = document.createElement('canvas');
  plateCanvas.width = 512;
  plateCanvas.height = 128;
  const pg = plateCanvas.getContext('2d')!;
  pg.fillStyle = '#0b0e18';
  pg.fillRect(0, 0, 512, 128);
  pg.strokeStyle = 'rgba(217, 169, 63, 0.85)';
  pg.lineWidth = 5;
  pg.strokeRect(10, 10, 492, 108);
  pg.fillStyle = '#e9bc55';
  pg.textAlign = 'center';
  pg.font = 'italic 900 44px system-ui, sans-serif';
  pg.fillText('DRIBBLE RACER', 256, 56);
  pg.font = '700 26px system-ui, sans-serif';
  pg.fillText('W O R L D   T O U R   C U P', 256, 98);
  const plateTex = new THREE.CanvasTexture(plateCanvas);
  plateTex.colorSpace = THREE.SRGBColorSpace;
  const plateMat = new THREE.MeshStandardMaterial({
    map: plateTex,
    metalness: 0.6,
    roughness: 0.35,
    emissive: 0xffffff,
    emissiveMap: plateTex,
    emissiveIntensity: 0.22, // faint self-light so the engraving reads at night
  });
  materials.push(plateMat);
  const plate = add(new THREE.PlaneGeometry(1.5, 0.36), plateMat, 0.55);
  plate.position.z = 1.72; // hugs the upper green band's face (r 1.7)

  return {
    group,
    cradle: new THREE.Vector3(0, BALL_CENTER_Y, 0),
    globeRadius: GLOBE_SEAT_RADIUS,
    height: BALL_CENTER_Y + GLOBE_SEAT_RADIUS,
    dispose(): void {
      for (const g of geometries) g.dispose();
      for (const m of materials) m.dispose();
    },
  };
}
